import type { UiCaptureTarget } from 'ts-components/PiChat/protocol';
import {
  CaptureError,
  NOT_RENDERED_GRACE_MS,
  captureElement,
  nextPaint,
  replyWithCapture,
  type CaptureRegion,
} from 'ts-components/PiChat/capture/captureElement';
import type {
  CaptureContext,
  CaptureMessage,
} from 'ts-components/PiChat/capture/captureRequest';
import {
  CAPTURE_APP_ATTRIBUTE,
  CAPTURE_FRAME_ATTRIBUTE,
  CAPTURE_PAGE_ATTRIBUTE,
  CAPTURE_PENDING_ATTRIBUTE,
  floatingFrame,
  floatingFrameId,
} from 'ts-components/Plugins/floatingFrameRegistry';

export type UiCaptureMessage = Extract<CaptureMessage, { type: 'ui.capture' }>;

export type CaptureLookup =
  | { element: HTMLElement; pending: boolean }
  | 'unsupported'
  | null;

type CaptureRoot = Pick<ParentNode, 'querySelectorAll'>;

export const findCaptureTarget = (
  root: CaptureRoot,
  target: UiCaptureTarget,
  appApiName: string | undefined
): CaptureLookup => {
  if (appApiName === undefined) return null;
  const [marker, owner, expected] =
    target.kind === 'page'
      ? [CAPTURE_PAGE_ATTRIBUTE, CAPTURE_APP_ATTRIBUTE, appApiName]
      : [
          CAPTURE_FRAME_ATTRIBUTE,
          CAPTURE_FRAME_ATTRIBUTE,
          floatingFrameId(appApiName, target.frameApiName),
        ];
  const element = Array.from(
    root.querySelectorAll<HTMLElement>(`[${marker}]`)
  ).find((candidate) => candidate.getAttribute(owner) === expected);
  if (!element) return null;
  if (element.getAttribute(CAPTURE_PAGE_ATTRIBUTE) === 'iframe') {
    return 'unsupported';
  }
  return { element, pending: element.hasAttribute(CAPTURE_PENDING_ATTRIBUTE) };
};

export const isRendered = (element: HTMLElement) => {
  const { width, height } = element.getBoundingClientRect();
  return (
    width > 0 &&
    height > 0 &&
    element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
  );
};

const frameRegion = ({
  width,
  height,
}: Pick<DOMRect, 'width' | 'height'>): CaptureRegion => ({
  width,
  height,
  style: { transform: 'none' },
});

const pageRegion = (
  { top, width, height }: Pick<DOMRect, 'top' | 'width' | 'height'>,
  scrollHeight: number,
  viewportHeight: number,
  fullPage: boolean
): CaptureRegion => {
  const contentHeight = Math.max(height, scrollHeight);
  if (fullPage) return { width, height: contentHeight };
  return {
    width,
    height: Math.max(
      Math.min(top + contentHeight, viewportHeight) - Math.max(top, 0),
      0
    ),
    style: {
      height: `${contentHeight}px`,
      transform: `translateY(${Math.min(top, 0)}px)`,
    },
  };
};

export const waitForCaptureTarget = async (
  lookup: () => CaptureLookup,
  minimized: () => boolean,
  name: string,
  signal: AbortSignal
): Promise<HTMLElement> => {
  let missingSince = Date.now();
  while (!signal.aborted) {
    const found = lookup();
    if (found === 'unsupported') {
      throw new CaptureError('unsupported', `${name} is an iframe`);
    }
    if (found?.pending) {
      missingSince = Date.now();
    } else if (found && isRendered(found.element)) {
      return found.element;
    } else if (!found && minimized()) {
      throw new CaptureError('minimized', `${name} is minimized`);
    } else if (Date.now() - missingSince >= NOT_RENDERED_GRACE_MS) {
      throw new CaptureError('not_rendered', `${name} is not rendered`);
    }
    await nextPaint();
  }
  throw new CaptureError('capture_failed', 'Capture was cancelled');
};

/** Reads the app binding on every poll, so a request made before the session binds still finds the bound app's target. */
export const kiCodeCaptureLookup = (
  root: CaptureRoot,
  target: UiCaptureTarget,
  appApiName: () => string | undefined
) => ({
  lookup: () => findCaptureTarget(root, target, appApiName()),
  minimized: () => {
    const app = appApiName();
    return (
      target.kind === 'floating_frame' &&
      app !== undefined &&
      floatingFrame(floatingFrameId(app, target.frameApiName))?.minimized ===
        true
    );
  },
});

export const captureKiCodeTarget = (
  { target, fullPage }: UiCaptureMessage,
  appApiName: () => string | undefined,
  { signal, reply }: CaptureContext
) =>
  replyWithCapture(reply, async () => {
    const name =
      target.kind === 'page'
        ? 'The plugin page'
        : `Floating frame ${target.frameApiName}`;
    const { lookup, minimized } = kiCodeCaptureLookup(
      document,
      target,
      appApiName
    );
    const element = await waitForCaptureTarget(lookup, minimized, name, signal);
    return captureElement(element, {
      name,
      region:
        target.kind === 'page'
          ? (rect) =>
              pageRegion(
                rect,
                element.scrollHeight,
                window.innerHeight,
                Boolean(fullPage)
              )
          : frameRegion,
    });
  });
